import { mixColor, type Color } from "../../common/color.js";
import type { KeyEventArgs, MouseEventArgs, TextInputArgs } from "../core/events.js";
import { clamp, type Point, type Rect, type Size } from "../../common/geometry.js";
import type { FontSpec } from "../render/GlyphAtlas.js";
import type { RenderContext } from "../render/RenderContext.js";
import { wrapLineOffsets } from "../render/TextLayout.js";
import type { Theme } from "../themes/Theme.js";
import { ScrollableBase, type ScrollableOptions } from "./ScrollableBase.js";
import {
  CARET_ROOM,
  UndoStack,
  stepIndex,
  wordAt,
  wordBoundary,
  type EditKind,
  type EditState,
} from "./textEditing.js";

/**
 * A colored run of the buffer, in buffer offsets.
 *
 * Offsets rather than (line, column) because a highlighter reads the whole
 * text: a string literal or a comment can span lines, and a per-line colorer
 * cannot see that it is inside one.
 */
export interface TextRun {
  start: number;
  /** Exclusive. */
  end: number;
  color: Color;
}

/**
 * Colors a buffer. Runs must be sorted by `start` and must not overlap;
 * anything not covered by a run is drawn in the control's normal ink.
 *
 * Called once per change rather than once per frame, so a real tokenizer is
 * an affordable thing to put here.
 */
export type TextHighlighter = (text: string) => readonly TextRun[];

export interface TextAreaOptions extends ScrollableOptions {
  text?: string;
  font?: FontSpec;
  placeholder?: string;
  readOnly?: boolean;
  maxLength?: number;
  /** Break long lines to the viewport instead of scrolling sideways. */
  wrap?: boolean;
  /** Tab inserts a tab character rather than moving focus to the next control. */
  acceptsTab?: boolean;
  /** Number the lines down a gutter on the left. */
  lineNumbers?: boolean;
  /** Wash the row the caret is on, the way a code editor does. */
  highlightCurrentLine?: boolean;
  /** Syntax coloring. Null (the default) draws everything in the theme's ink. */
  highlight?: TextHighlighter | null;
  /** Steps Ctrl+Z can give back. 0 turns undo off entirely. */
  undoLimit?: number;
  onChange?: () => void;
}

const CARET_BLINK_MS = 530;
/** Never reach the buffer. Tab and newline are deliberately not in the set. */
const CONTROL_CHARS = new RegExp("[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]", "g");
const NEWLINE = "\n";
const TAB = "\t";
/** Inset of the text from the viewport edge, so the caret is never flush. */
const PAD_X = 4;
const PAD_Y = 3;
// `CARET_ROOM` is in `textEditing.ts`: the room past the last glyph that makes the end of a
// line somewhere the caret can be seen. `contentSize`, `#ensureCaretVisible` and wrapping all
// measure from it, and so does `TextBox`.
/**
 * Space either side of the line numbers.
 *
 * The right-hand one is what separates them from the text, and is why the gutter needs no rule
 * drawn down it - the same answer Visual Studio's own margin gives, and this editor is already
 * wearing Visual Studio's colors.
 */
const GUTTER_PAD = 6;
/**
 * Digits the gutter is sized for however few lines there are.
 *
 * A one-line buffer would otherwise get a gutter barely wider than a single character, and then
 * widen the moment a second line was typed - the text stepping sideways under the caret.
 */
const MIN_DIGITS = 2;
/** How far the current-line wash is mixed from the field towards its ink. */
const CURRENT_LINE_MIX = 0.07;
/** Shared empty result, so an un-highlighted control allocates nothing. */
const EMPTY_RUNS: readonly TextRun[] = [];
/** Deep enough that a session of editing is never truncated in practice. */
const DEFAULT_UNDO_LIMIT = 200;
const TOP = { baseline: "top" } as const;

/** One visual line: a range of the buffer, never including its newline. */
interface Line {
  start: number;
  end: number;
  /**
   * 1-based line of the *file*, or 0 for a row that is the continuation of a wrapped one.
   *
   * Carried on the row rather than counted when the gutter paints, because counting means
   * walking the buffer from the top on every frame. It falls out of the walk that builds the
   * index anyway.
   */
  number: number;
}

/**
 * A multi-line editable text control.
 *
 * `TextBox` is deliberately one-dimensional — its caret is an index into a
 * flat string and everything downstream assumes it. Rather than bolt a mode
 * onto it, this is a sibling built on `ScrollableBase`, which brings the
 * scroll bars, the wheel and two-finger panning for nothing.
 *
 * The buffer stays a flat string and the caret stays a single index into it,
 * which is what keeps the clipboard, the IME and the hidden textarea simple.
 * Lines are a derived index rebuilt when the text, the font or the wrap width
 * changes, so (line, column) is computed rather than stored.
 */
export class TextArea extends ScrollableBase {
  #buffer = "";
  /**
   * Bumped on every change to the buffer. The derived caches key on this
   * rather than on the text: a key built from the whole buffer cost a copy of
   * the document on every read of `lines`, a dozen times a frame.
   */
  #version = 0;

  get #text(): string {
    return this.#buffer;
  }
  set #text(v: string) {
    this.#buffer = v;
    this.#version++;
  }
  #font: FontSpec | null;
  #wrap: boolean;
  #lineNumbers: boolean;
  #caret = 0;
  #anchor = 0;
  #caretOn = true;
  #blinkTimer = 0;
  #composition = "";
  #dragging = false;
  #wordDrag: { start: number; end: number } | null = null;
  /**
   * Column to aim for while moving up and down, in pixels.
   *
   * Held across successive Up/Down presses, so passing through a short line
   * does not drag the caret permanently left.
   */
  #goalX: number | null = null;

  /** Derived line index, and the inputs it was built from. */
  #lines: Line[] = [{ start: 0, end: 0, number: 1 }];
  #linesKey = "";

  /** Lines of the *file*, cached separately: the gutter's width needs it before the index does. */
  #hardLines = 1;
  #hardLinesKey = -1;
  /** Gutter width, and the digit count and font it was measured for. */
  #gutter = 0;
  #gutterKey = "";

  /** Derived coloring, and the buffer it was built from. */
  #highlight: TextHighlighter | null = null;
  #runs: readonly TextRun[] = EMPTY_RUNS;
  #runsKey = -1;

  #history: UndoStack | null;
  /** Set while a snapshot is being put back, so undo does not record its own work. */
  #restoring = false;

  placeholder: string;
  readOnly: boolean;
  maxLength: number;
  acceptsTab: boolean;
  /**
   * Plain fields, where `wrap` and `lineNumbers` are accessors: this one changes nothing but
   * what a frame looks like, where those two move the text and so rebuild the line index.
   */
  highlightCurrentLine: boolean;

  constructor(options: TextAreaOptions = {}) {
    super({ width: 320, height: 160, tabStop: true, cursor: "text", ...options });
    this.#text = this.#sanitize(options.text ?? "");
    this.#font = options.font ?? null;
    this.#wrap = options.wrap ?? false;
    this.placeholder = options.placeholder ?? "";
    this.readOnly = options.readOnly ?? false;
    this.maxLength = options.maxLength ?? Number.MAX_SAFE_INTEGER;
    this.acceptsTab = options.acceptsTab ?? false;
    this.#lineNumbers = options.lineNumbers ?? false;
    this.highlightCurrentLine = options.highlightCurrentLine ?? false;
    this.#highlight = options.highlight ?? null;
    const undoLimit = options.undoLimit ?? DEFAULT_UNDO_LIMIT;
    this.#history = undoLimit > 0 ? new UndoStack(undoLimit) : null;
    this.#caret = this.#anchor = this.#text.length;
    if (options.onChange) this.on("change", options.onChange);

    this.on("pointerdown", (e) => this.#onPointerDown(e));
    this.on("pointermove", (e) => this.#onPointerMove(e));
    this.on("pointerup", () => this.#onPointerUp());
    this.on("doubleclick", (e) => this.#onDoubleClick(e));
    this.on("keydown", (e) => this.onKeyDown(e));
    this.on("textinput", (e) => this.#onTextInput(e));
    this.on("focus", () => this.#onFocus());
    this.on("blur", () => this.#onBlur());
  }

  /** Normalize line endings and strip what must never enter the buffer. */
  #sanitize(v: string): string {
    return v.replace(/\r\n?/g, NEWLINE).replace(CONTROL_CHARS, "");
  }

  // --- value --------------------------------------------------------------

  get text(): string {
    return this.#text;
  }
  set text(v: string) {
    const next = this.#sanitize(v).slice(0, this.maxLength);
    if (this.#text === next) return;
    // Replacing the whole buffer is one step, never part of a typing run.
    this.#record("other", this.#caret, Math.min(this.#caret, next.length));
    this.#text = next;
    this.#caret = this.#anchor = Math.min(this.#caret, next.length);
    this.#afterEdit();
  }

  /** The buffer split on hard newlines, whatever the wrap setting. */
  get textLines(): string[] {
    return this.#text.split(NEWLINE);
  }

  get font(): FontSpec | null {
    return this.#font;
  }
  set font(v: FontSpec | null) {
    this.#font = v;
    this.#linesKey = "";
    this.updateScrollBars();
    this.invalidate();
  }

  /**
   * The colorer, or null for none.
   *
   * Setting it discards the cached runs, which is also how a caller re-colors
   * after a theme change: hand it a new closure over the new palette.
   */
  get highlight(): TextHighlighter | null {
    return this.#highlight;
  }
  set highlight(v: TextHighlighter | null) {
    this.#highlight = v;
    this.#runsKey = -1;
    this.invalidate();
  }

  // --- undo -----------------------------------------------------------------

  get canUndo(): boolean {
    return this.#history?.canUndo ?? false;
  }

  get canRedo(): boolean {
    return this.#history?.canRedo ?? false;
  }

  /**
   * Forget the history.
   *
   * What a caller wants after loading a different document into the control: the steps before
   * it belong to text that is no longer here.
   */
  clearUndo(): void {
    this.#history?.clear();
  }

  /**
   * End the current run of keystrokes.
   *
   * Undo already breaks a run when the caret moves; this is for a caller that knows something
   * else made the next edit a separate action.
   */
  breakUndoRun(): void {
    this.#history?.breakRun();
  }

  /**
   * Fold the next edit into the step before it.
   *
   * The other way round from `breakUndoRun`, and for a caller that is about to make an edit on
   * the author's behalf rather than at their request - laying out what they just pasted, say.
   * Undo then gives back what was there before the paste rather than the paste itself.
   */
  joinUndoRun(): void {
    this.#history?.joinNext();
  }

  /** Go back one step. False when there was nothing to go back to. */
  undo(): boolean {
    return this.#restore(this.#history?.undo(this.#state()) ?? null);
  }

  redo(): boolean {
    return this.#restore(this.#history?.redo(this.#state()) ?? null);
  }

  #state(): EditState {
    return { text: this.#text, caret: this.#caret, anchor: this.#anchor };
  }

  #restore(state: EditState | null): boolean {
    if (!state) return false;
    this.#restoring = true;
    try {
      this.#text = state.text;
      this.#caret = Math.min(state.caret, state.text.length);
      this.#anchor = Math.min(state.anchor, state.text.length);
      this.#afterEdit();
    } finally {
      this.#restoring = false;
    }
    return true;
  }

  /** Snapshot the buffer as it stands, before an edit that is about to change it. */
  #record(kind: EditKind, from: number, to: number): void {
    if (this.#restoring) return;
    this.#history?.record(this.#state(), kind, from, to);
  }

  /** Break long lines to the viewport rather than scrolling sideways. */
  get wrap(): boolean {
    return this.#wrap;
  }
  set wrap(v: boolean) {
    if (this.#wrap === v) return;
    this.#wrap = v;
    this.#linesKey = "";
    this.updateScrollBars();
    this.#ensureCaretVisible();
    this.invalidate();
  }

  /**
   * Number the lines down a gutter on the left.
   *
   * An accessor rather than a plain field because the gutter takes its width out of the text:
   * turning it on moves every line along, which is a different wrap and a different scroll
   * extent.
   */
  get lineNumbers(): boolean {
    return this.#lineNumbers;
  }
  set lineNumbers(v: boolean) {
    if (this.#lineNumbers === v) return;
    this.#lineNumbers = v;
    this.#linesKey = "";
    this.updateScrollBars();
    this.#ensureCaretVisible();
    this.invalidate();
  }

  override get acceptsTextInput(): boolean {
    return true;
  }

  // --- selection ----------------------------------------------------------

  get caretIndex(): number {
    return this.#caret;
  }
  get selectionStart(): number {
    return Math.min(this.#anchor, this.#caret);
  }
  get selectionEnd(): number {
    return Math.max(this.#anchor, this.#caret);
  }
  get hasSelection(): boolean {
    return this.#anchor !== this.#caret;
  }
  get selectedText(): string {
    return this.#text.slice(this.selectionStart, this.selectionEnd);
  }
  /**
   * Replace the selection, or insert at the caret when there is none.
   *
   * The programmatic half of typing, which is what anything driving the buffer from outside
   * needs: a completion list, a snippet, a "paste this in" command.
   */
  set selectedText(v: string) {
    if (this.readOnly) return;
    this.#replaceSelection(v);
  }

  select(start: number, end: number): void {
    const anchor = clamp(start, 0, this.#text.length);
    const caret = clamp(end, 0, this.#text.length);
    const moved = anchor !== this.#anchor || caret !== this.#caret;
    this.#anchor = anchor;
    this.#caret = caret;
    this.#goalX = null;
    this.#restartBlink();
    this.#ensureCaretVisible();
    this.#syncMirror();
    if (moved) this.emit("selectionchanged");
    this.invalidate();
  }

  selectAll(): void {
    this.select(0, this.#text.length);
  }

  /** Caret position in the buffer, as a zero-based line and column. */
  get caretLocation(): { line: number; column: number } {
    const before = this.#text.slice(0, this.#caret).split(NEWLINE);
    return { line: before.length - 1, column: before[before.length - 1]!.length };
  }

  // --- line index ---------------------------------------------------------

  /** Baseline-to-baseline distance for one line. */
  get lineHeight(): number {
    const desktop = this.desktop;
    if (!desktop) return 18;
    return desktop.rc.lineHeight(this.#themeFont(desktop.theme));
  }

  /** Width available for text, once padding, the gutter and any wrap are accounted for. */
  get #textWidth(): number {
    return Math.max(0, this.viewportRect.width - this.textLeft - CARET_ROOM);
  }

  /**
   * Lines in the file, which is not `lines.length` once anything wraps.
   *
   * Kept apart from the line index because the gutter's width is measured from it, and with
   * `wrap` on that width is an *input* to the index - asking the index for it would be a loop.
   */
  get #hardLineCount(): number {
    if (this.#hardLinesKey !== this.#version) {
      this.#hardLinesKey = this.#version;
      let n = 1;
      for (let i = this.#text.indexOf(NEWLINE); i >= 0; i = this.#text.indexOf(NEWLINE, i + 1)) n++;
      this.#hardLines = n;
    }
    return this.#hardLines;
  }

  /** The gutter's width, or 0 when there is no gutter. */
  get gutterWidth(): number {
    if (!this.#lineNumbers) return 0;
    const digits = Math.max(MIN_DIGITS, String(this.#hardLineCount).length);
    const font = this.#font ?? this.desktop?.theme.metrics.font;
    const key = `${digits}|${font?.family}|${font?.size}`;
    if (key !== this.#gutterKey) {
      this.#gutterKey = key;
      // Zeros rather than the widest digit actually present: the gutter must not change width
      // as the caret runs down a file whose line 108 is wider than its line 111.
      this.#gutter = Math.ceil(this.#measure("0".repeat(digits))) + GUTTER_PAD * 2;
    }
    return this.#gutter;
  }

  /** Where the text starts, in content coordinates: the gutter, then the usual inset. */
  get textLeft(): number {
    return this.gutterWidth + PAD_X;
  }

  /**
   * Visual lines, rebuilt only when something they depend on changes.
   *
   * The key is what makes that cheap: typing rebuilds the index because the
   * text changed, but scrolling and repainting do not.
   */
  get lines(): Line[] {
    const width = this.#wrap ? Math.round(this.#textWidth) : -1;
    const font = this.#font ?? this.desktop?.theme.metrics.font;
    const key = `${this.#version}|${width}|${font?.family}|${font?.size}`;
    if (key !== this.#linesKey) {
      this.#linesKey = key;
      this.#lines = this.#buildLines(width);
    }
    return this.#lines;
  }

  #buildLines(width: number): Line[] {
    const out: Line[] = [];
    const text = this.#text;
    let start = 0;
    let number = 1;
    for (;;) {
      const nl = text.indexOf(NEWLINE, start);
      const end = nl < 0 ? text.length : nl;
      if (width > 0) {
        const breaks = wrapLineOffsets(text, start, end, width, (a, b) => this.#measure(text.slice(a, b)));
        for (let i = 0; i < breaks.length; i++) {
          // Only the first row of a wrapped line is numbered. The rest are the same line of the
          // file, and numbering them would be counting something the file does not have.
          out.push({
            start: breaks[i]!,
            end: i + 1 < breaks.length ? breaks[i + 1]! : end,
            number: i === 0 ? number : 0,
          });
        }
      } else {
        out.push({ start, end, number });
      }
      number++;
      if (nl < 0) break;
      start = nl + 1;
    }
    return out.length > 0 ? out : [{ start: 0, end: 0, number: 1 }];
  }

  #themeFont(theme: Theme): FontSpec {
    return this.#font ?? theme.metrics.font;
  }

  #measure(s: string): number {
    const desktop = this.desktop;
    if (!desktop || !s) return 0;
    return desktop.rc.measureText(s, this.#themeFont(desktop.theme)).width;
  }

  /** Index of the visual line containing `index`. */
  lineAt(index: number): number {
    const lines = this.lines;
    let lo = 0;
    let hi = lines.length - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (lines[mid]!.start <= index) lo = mid;
      else hi = mid - 1;
    }
    return lo;
  }

  /** Caret position in content coordinates, before scrolling is applied. */
  caretPoint(index = this.#caret): Point {
    const row = this.lineAt(index);
    const line = this.lines[row]!;
    return {
      x: this.textLeft + this.#measure(this.#text.slice(line.start, clamp(index, line.start, line.end))),
      y: PAD_Y + row * this.lineHeight,
    };
  }

  /** Buffer index nearest a point in content coordinates. */
  indexAtPoint(p: Point): number {
    const lines = this.lines;
    const row = clamp(Math.floor((p.y - PAD_Y) / this.lineHeight), 0, lines.length - 1);
    return this.#indexOnLine(row, p.x);
  }

  /** Index on `row` closest to content x, snapping to whole code points. */
  #indexOnLine(row: number, x: number): number {
    const line = this.lines[row];
    if (!line) return 0;
    const target = x - this.textLeft;
    if (target <= 0) return line.start;
    let best = line.start;
    let bestDelta = Math.abs(target);
    for (let i = line.start; i < line.end; ) {
      const next = stepIndex(this.#text, i, 1);
      const delta = Math.abs(this.#measure(this.#text.slice(line.start, next)) - target);
      if (delta < bestDelta) {
        bestDelta = delta;
        best = next;
      }
      i = next;
    }
    return best;
  }

  protected override contentSize(): Size {
    const lines = this.lines;
    let widest = 0;
    if (!this.#wrap) {
      for (const line of lines) {
        widest = Math.max(widest, this.#measure(this.#text.slice(line.start, line.end)));
      }
    }
    return {
      width: widest + this.textLeft + CARET_ROOM,
      height: lines.length * this.lineHeight + PAD_Y * 2,
    };
  }

  // --- editing ------------------------------------------------------------

  /** Everything that has to happen after the buffer changes. */
  #afterEdit(): void {
    this.#goalX = null;
    this.#linesKey = "";
    this.#restartBlink();
    this.updateScrollBars();
    this.#ensureCaretVisible();
    this.emit("textchanged");
    this.emit("change");
    this.emit("selectionchanged");
    this.#syncMirror();
    this.invalidate();
  }

  /** Replace the selection, or insert at the caret. */
  #replaceSelection(insert: string): void {
    if (this.readOnly) return;
    const start = this.selectionStart;
    const end = this.selectionEnd;
    const room = this.maxLength - (this.#text.length - (end - start));
    const clipped = this.#sanitize(insert).slice(0, Math.max(0, room));
    if (clipped.length === 0 && start === end) return;

    // Which kind of run this edit could join. Only a plain keystroke joins one: replacing a
    // selection, pasting, and pressing Enter are each a step a person would expect back whole.
    const caretBefore = this.#caret;
    const kind: EditKind =
      start !== end || clipped.includes(NEWLINE) || clipped.length > 1
        ? "other"
        : clipped.length === 1
          ? "insert"
          : "delete";
    this.#record(kind, caretBefore, start + clipped.length);

    this.#text = this.#text.slice(0, start) + clipped + this.#text.slice(end);
    this.#caret = this.#anchor = start + clipped.length;
    this.#afterEdit();
  }

  #deleteSelection(): boolean {
    if (!this.hasSelection || this.readOnly) return false;
    this.#replaceSelection("");
    return true;
  }

  #moveCaret(index: number, extend: boolean, keepGoal = false): void {
    const before = `${this.#caret}/${this.#anchor}`;
    this.#caret = clamp(index, 0, this.#text.length);
    if (!extend) this.#anchor = this.#caret;
    if (!keepGoal) this.#goalX = null;
    this.#restartBlink();
    this.#ensureCaretVisible();
    this.#syncMirror();
    if (before !== `${this.#caret}/${this.#anchor}`) this.emit("selectionchanged");
    this.invalidate();
  }

  /** Move `rows` visual lines, holding the column the caret was aiming for. */
  #moveByLine(rows: number, extend: boolean): void {
    const from = this.lineAt(this.#caret);
    const goal = this.#goalX ?? this.caretPoint().x;
    const to = clamp(from + rows, 0, this.lines.length - 1);
    if (to === from) {
      // Already at the top or bottom: go to the very start or end, which is
      // what a caret does when it cannot move a whole line.
      this.#moveCaret(rows < 0 ? 0 : this.#text.length, extend);
      return;
    }
    this.#moveCaret(this.#indexOnLine(to, goal), extend, true);
    this.#goalX = goal;
  }

  /** Rows that fit in the viewport, for PageUp and PageDown. */
  get #pageRows(): number {
    return Math.max(1, Math.floor(this.viewportRect.height / this.lineHeight) - 1);
  }

  // --- scrolling ----------------------------------------------------------

  #ensureCaretVisible(): void {
    const view = this.viewportRect;
    if (view.width <= 0 || view.height <= 0) return;
    const at = this.caretPoint();
    const h = this.lineHeight;
    // The left inset includes the gutter, which is painted over the text: without it a caret
    // scrolled to would count as visible while sitting underneath the line numbers.
    const left = this.textLeft;
    this.scrollIntoView({ x: at.x - left, y: at.y, width: left + CARET_ROOM, height: h });
  }

  protected override onScrollChanged(): void {
    super.onScrollChanged();
    this.#syncMirror();
  }

  // --- clipboard, IME and the hidden field --------------------------------

  /**
   * Called by the Desktop when the hidden field's text changed: replace
   * [start, end) with `text`. This is how a phone keyboard edits, since it
   * rewrites words in place rather than sending keystrokes.
   */
  replaceTextRange(start: number, end: number, text: string): void {
    if (this.readOnly) return;
    const length = this.#text.length;
    const from = Math.max(0, Math.min(start, length));
    const to = Math.max(from, Math.min(end, length));
    this.select(from, to);
    this.#replaceSelection(text);
    // Whatever the control refused (past maxLength) has to leave the field
    // too, or the next edit's offsets are off by it.
    this.#syncMirror();
  }

  /** Called by the Desktop for a native copy or cut. */
  getClipboardText(cut: boolean): string | null {
    if (!this.hasSelection) return null;
    const text = this.selectedText;
    if (cut && !this.readOnly) this.#replaceSelection("");
    return text;
  }

  /** Called by the Desktop while an IME composition is in flight. */
  setComposition(text: string): void {
    this.#composition = text;
    this.invalidate();
  }

  /**
   * Mirror the selection into the hidden textarea so the browser's own copy
   * and cut carry the right payload, and park it at the caret so an IME
   * candidate window opens in the right place.
   */
  #syncMirror(): void {
    const desktop = this.desktop;
    if (!desktop || !this.focused) return;
    desktop.keyboard.setMirroredText(this.#text, this.selectionStart, this.selectionEnd);
    const at = this.caretPoint();
    const view = this.viewportRect;
    desktop.keyboard.setCaretPosition(
      this.toScreen({
        x: view.x + at.x - this.scrollOffsetX,
        y: view.y + at.y - this.scrollOffsetY + this.lineHeight,
      }),
    );
  }

  // --- caret blink --------------------------------------------------------

  #restartBlink(): void {
    this.#caretOn = true;
    if (this.#blinkTimer !== 0) clearInterval(this.#blinkTimer);
    if (!this.focused) return;
    this.#blinkTimer = setInterval(() => {
      this.#caretOn = !this.#caretOn;
      this.invalidate();
    }, CARET_BLINK_MS) as unknown as number;
  }

  #stopBlink(): void {
    if (this.#blinkTimer !== 0) clearInterval(this.#blinkTimer);
    this.#blinkTimer = 0;
    this.#caretOn = false;
  }

  #onFocus(): void {
    this.#restartBlink();
    this.#syncMirror();
    this.invalidate();
  }

  #onBlur(): void {
    this.#stopBlink();
    this.#composition = "";
    this.invalidate();
  }

  // --- pointer ------------------------------------------------------------

  /** Pointer position in content coordinates. */
  #toContent(e: MouseEventArgs): Point {
    const view = this.viewportRect;
    return {
      x: e.x - view.x + this.scrollOffsetX,
      y: e.y - view.y + this.scrollOffsetY,
    };
  }

  #onPointerDown(e: MouseEventArgs): void {
    if (e.button !== 0 || this.inputBlocked) return;
    const view = this.viewportRect;
    if (e.x > view.x + view.width || e.y > view.y + view.height) return; // a scroll bar
    e.handled = true;
    this.focus();
    const index = this.indexAtPoint(this.#toContent(e));
    // Through `#moveCaret` like every other way of moving it, which is what raises
    // `selectionchanged`. A click is the commonest of them, and it used to be the one that said
    // nothing: anything listening for the caret to move - a status bar, a pane keeping itself in
    // step with where the author is working - heard every arrow key and no click at all.
    this.#moveCaret(index, e.shiftKey);
    this.#dragging = true;
    this.#wordDrag = null;
    this.desktop?.input.setCapture(this);
  }

  #onPointerMove(e: MouseEventArgs): void {
    if (!this.#dragging) return;
    const index = this.indexAtPoint(this.#toContent(e));
    if (this.#wordDrag) {
      // Started as a double-click, so keep extending whole words.
      const word = wordAt(this.#text, index);
      const seed = this.#wordDrag;
      if (index < seed.start) this.select(seed.end, word.start);
      else this.select(seed.start, Math.max(word.end, seed.end));
      return;
    }
    this.#moveCaret(index, true);
  }

  #onPointerUp(): void {
    if (!this.#dragging) return;
    this.#dragging = false;
    this.#wordDrag = null;
    this.desktop?.input.releaseCapture(this);
  }

  #onDoubleClick(e: MouseEventArgs): void {
    const index = this.indexAtPoint(this.#toContent(e));
    if (e.clickCount >= 3) {
      // Triple-click takes the whole visual line, newline excluded.
      const line = this.lines[this.lineAt(index)]!;
      this.select(line.start, line.end);
      this.#wordDrag = null;
      return;
    }
    const word = wordAt(this.#text, index);
    this.select(word.start, word.end);
    this.#wordDrag = word;
    e.handled = true;
  }

  // --- keyboard -----------------------------------------------------------

  #onTextInput(e: TextInputArgs): void {
    if (this.readOnly) return;
    const text = this.#sanitize(e.text);
    if (!text) return;
    this.#replaceSelection(text);
    e.handled = true;
  }

  /**
   * The editing keys.
   *
   * Protected rather than private so a subclass can take a key first — an
   * editor with a completion list needs the arrows and Enter while it is
   * open, and a handler added with `on("keydown")` cannot get them, because
   * this one is registered in the constructor and therefore runs first.
   */
  protected onKeyDown(e: KeyEventArgs): void {
    if (e.handled || this.inputBlocked) return;
    const extend = e.shiftKey;
    const word = e.ctrlKey || e.metaKey;
    const t = this.#text;

    switch (e.key) {
      case "ArrowLeft":
        this.#moveCaret(
          word ? wordBoundary(t, this.#caret, -1) : this.#collapseOr(-1, extend),
          extend,
        );
        break;
      case "ArrowRight":
        this.#moveCaret(
          word ? wordBoundary(t, this.#caret, 1) : this.#collapseOr(1, extend),
          extend,
        );
        break;
      case "ArrowUp":
        this.#moveByLine(-1, extend);
        break;
      case "ArrowDown":
        this.#moveByLine(1, extend);
        break;
      case "PageUp":
        this.#moveByLine(-this.#pageRows, extend);
        break;
      case "PageDown":
        this.#moveByLine(this.#pageRows, extend);
        break;
      case "Home":
        this.#moveCaret(word ? 0 : this.lines[this.lineAt(this.#caret)]!.start, extend);
        break;
      case "End":
        this.#moveCaret(word ? t.length : this.lines[this.lineAt(this.#caret)]!.end, extend);
        break;
      case "Backspace":
        if (this.readOnly) break;
        if (!this.#deleteSelection()) {
          const from = word ? wordBoundary(t, this.#caret, -1) : stepIndex(t, this.#caret, -1);
          if (from !== this.#caret) this.select(from, this.#caret), this.#replaceSelection("");
        }
        break;
      case "Delete":
        if (this.readOnly) break;
        if (!this.#deleteSelection()) {
          const to = word ? wordBoundary(t, this.#caret, 1) : stepIndex(t, this.#caret, 1);
          if (to !== this.#caret) this.select(this.#caret, to), this.#replaceSelection("");
        }
        break;
      case "Enter":
        if (this.readOnly) break;
        this.#replaceSelection(NEWLINE);
        break;
      case "Tab":
        // Off by default: swallowing Tab traps keyboard users in the control.
        if (!this.acceptsTab || this.readOnly || word) return;
        this.#replaceSelection(TAB);
        break;
      case "a":
      case "A":
        if (!word) return;
        this.selectAll();
        break;
      case "z":
      case "Z":
        // Ctrl+Shift+Z is redo everywhere but Windows, and harmless there.
        if (!word || this.readOnly) return;
        if (!(e.shiftKey ? this.redo() : this.undo())) return;
        break;
      case "y":
      case "Y":
        if (!word || this.readOnly) return;
        if (!this.redo()) return;
        break;
      case "Escape":
        return;
      default:
        return;
    }
    e.handled = true;
  }

  /**
   * Where an unmodified arrow goes: to the edge of the selection when there
   * is one, otherwise one code point along.
   */
  #collapseOr(direction: 1 | -1, extend: boolean): number {
    if (this.hasSelection && !extend) {
      return direction < 0 ? this.selectionStart : this.selectionEnd;
    }
    return stepIndex(this.#text, this.#caret, direction);
  }

  // --- painting -----------------------------------------------------------

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const state = this.visualState();
    const p = theme.palette;
    theme.drawTextBox(rc, this.localRect, state);

    const view = this.viewportRect;
    if (view.width <= 0 || view.height <= 0) return;
    const font = this.#themeFont(theme);
    const h = this.lineHeight;
    const ink = state.disabled ? p.textDisabled : p.fieldText;
    const left = this.textLeft;
    // Everything the buffer draws is clipped to the right of the gutter, which is what keeps a
    // long line from sliding out through the line numbers when the view is scrolled sideways.
    // It also means the gutter needs no fill of its own, so the wash below shows through it.
    const gutter = this.gutterWidth;
    const textView: Rect = {
      x: view.x + gutter,
      y: view.y,
      width: Math.max(0, view.width - gutter),
      height: view.height,
    };

    this.#paintCurrentLine(rc, theme, view, h);

    if (!this.#text && !this.#composition && this.placeholder) {
      rc.clipped(textView, () => {
        rc.drawText(this.placeholder, view.x + left, view.y + PAD_Y, font, p.placeholderText, {
          baseline: "top",
        });
      });
      if (this.focused) this.#paintCaret(rc, theme, view, { x: left, y: PAD_Y });
      this.#paintGutter(rc, theme, view, font, h);
      return;
    }

    const lines = this.lines;
    const selStart = this.selectionStart;
    const selEnd = this.selectionEnd;
    const showSelection = this.hasSelection && this.focused;

    rc.clipped(textView, () => {
      const originX = view.x - this.scrollOffsetX;
      const originY = view.y - this.scrollOffsetY;
      // Only the rows on screen: a 10,000-line buffer paints the same handful.
      const first = this.#firstVisibleRow(h);
      const last = this.#lastVisibleRow(view, h);

      for (let row = first; row <= last; row++) {
        const line = lines[row]!;
        const y = originY + PAD_Y + row * h;
        const text = this.#text.slice(line.start, line.end);

        if (showSelection && selEnd > line.start && selStart <= line.end) {
          const from = Math.max(selStart, line.start);
          const to = Math.min(selEnd, line.end);
          const x0 = this.#measure(this.#text.slice(line.start, from));
          const x1 = this.#measure(this.#text.slice(line.start, to));
          // A selection running past the end of a line shows a sliver beyond
          // it, so a multi-line block reads as one shape rather than a ladder.
          const trailing = selEnd > line.end ? this.#measure(" ") : 0;
          rc.fillRect(
            { x: originX + left + x0, y, width: x1 - x0 + trailing, height: h },
            p.textSelectionBg,
          );
          // Positions come from the measured prefixes rather than from summing
          // the draws, so the three pieces line up with the selection box even
          // when a highlighter has split them further.
          this.#drawRange(rc, line.start, from, originX + left, y, font, ink, null);
          this.#drawRange(rc, from, to, originX + left + x0, y, font, ink, p.selectionText);
          this.#drawRange(rc, to, line.end, originX + left + x1, y, font, ink, null);
        } else if (text) {
          this.#drawRange(rc, line.start, line.end, originX + left, y, font, ink, null);
        }
      }

      const at = this.caretPoint();
      let caretX = at.x;
      if (this.#composition) {
        const w = this.#measure(this.#composition);
        const y = originY + at.y;
        rc.fillRect({ x: originX + caretX, y, width: w, height: h }, p.fieldBg);
        rc.drawText(this.#composition, originX + caretX, y, font, ink, { baseline: "top" });
        rc.hLine(originX + caretX, y + h - 1, w, ink);
        caretX += w;
      }
      if (this.focused) {
        this.#paintCaret(rc, theme, view, { x: caretX - this.scrollOffsetX, y: at.y - this.scrollOffsetY });
      }
    });

    this.#paintGutter(rc, theme, view, font, h);
  }

  /**
   * A wash across the row the caret is on.
   *
   * Only while focused, and only with nothing selected: it is there to say where typing will
   * land, and a selection says that already - better, and in a color this one would fight.
   * Mixed from the field towards its own ink rather than taken from a palette token, which is
   * what makes it come out lighter on a dark theme and darker on a light one without every
   * theme having to have an opinion about code editors.
   */
  #paintCurrentLine(rc: RenderContext, theme: Theme, view: Rect, h: number): void {
    if (!this.highlightCurrentLine || !this.focused || this.hasSelection) return;
    if (!this.effectivelyEnabled) return;
    const y = view.y - this.scrollOffsetY + PAD_Y + this.lineAt(this.#caret) * h;
    if (y + h <= view.y || y >= view.y + view.height) return;
    const p = theme.palette;
    rc.clipped(view, () => {
      rc.fillRect(
        { x: view.x, y, width: view.width, height: h },
        mixColor(p.fieldBg, p.text, CURRENT_LINE_MIX),
      );
    });
  }

  /**
   * The line numbers, right-aligned in their own column and drawn after the text.
   *
   * The caret's own line is in full ink where the rest are subtle, which is the cheapest way of
   * answering "which line am I on" in a file too long to count down.
   */
  #paintGutter(rc: RenderContext, theme: Theme, view: Rect, font: FontSpec, h: number): void {
    const width = this.gutterWidth;
    if (width <= 0) return;
    const p = theme.palette;
    const lines = this.lines;
    const caretRow = this.lineAt(this.#caret);
    const right = view.x + width - GUTTER_PAD;
    rc.clipped({ x: view.x, y: view.y, width, height: view.height }, () => {
      const originY = view.y - this.scrollOffsetY;
      for (let row = this.#firstVisibleRow(h); row <= this.#lastVisibleRow(view, h); row++) {
        const line = lines[row]!;
        if (line.number === 0) continue;
        const text = String(line.number);
        const current = row === caretRow && this.focused && this.effectivelyEnabled;
        rc.drawText(
          text,
          right - this.#measure(text),
          originY + PAD_Y + row * h,
          font,
          current ? p.text : p.textSubtle,
          TOP,
        );
      }
    });
  }

  /** First row that could be on screen. Only those are ever painted, gutter included. */
  #firstVisibleRow(h: number): number {
    return clamp(Math.floor((this.scrollOffsetY - PAD_Y) / h), 0, this.lines.length - 1);
  }

  #lastVisibleRow(view: Rect, h: number): number {
    return clamp(
      Math.ceil((this.scrollOffsetY + view.height - PAD_Y) / h),
      0,
      this.lines.length - 1,
    );
  }

  /** The coloring for the current buffer, computed at most once per change. */
  #highlightRuns(): readonly TextRun[] {
    if (!this.#highlight) return EMPTY_RUNS;
    if (this.#runsKey !== this.#version) {
      this.#runsKey = this.#version;
      this.#runs = this.#highlight(this.#text);
    }
    return this.#runs;
  }

  /**
   * Draw `[from, to)` of the buffer at `x`, in the colors the highlighter
   * asked for. `override` forces one color, which is what the selection
   * needs: legibility against the selection fill beats syntax color.
   *
   * Returns how far it advanced, so a caller can chain pieces.
   */
  #drawRange(
    rc: RenderContext,
    from: number,
    to: number,
    x: number,
    y: number,
    font: FontSpec,
    ink: Color,
    override: Color | null,
  ): number {
    if (to <= from) return 0;
    const t = this.#text;
    if (override) return rc.drawText(t.slice(from, to), x, y, font, override, TOP);

    const runs = this.#highlightRuns();
    if (runs.length === 0) return rc.drawText(t.slice(from, to), x, y, font, ink, TOP);

    let at = from;
    let advance = 0;
    // Runs are ordered and disjoint, so the first one reaching past `from` is
    // found by bisection rather than by walking every run above the viewport.
    let lo = 0;
    let hi = runs.length;
    while (lo < hi) {
      const mid = (lo + hi) >>> 1;
      if (runs[mid]!.end <= at) lo = mid + 1;
      else hi = mid;
    }
    for (let r = lo; r < runs.length; r++) {
      const run = runs[r]!;
      if (run.end <= at) continue;
      if (run.start >= to) break;
      if (run.start > at) {
        advance += rc.drawText(t.slice(at, run.start), x + advance, y, font, ink, TOP);
        at = run.start;
      }
      const end = Math.min(run.end, to);
      if (end > at) {
        advance += rc.drawText(t.slice(at, end), x + advance, y, font, run.color, TOP);
        at = end;
      }
    }
    if (at < to) advance += rc.drawText(t.slice(at, to), x + advance, y, font, ink, TOP);
    return advance;
  }

  #paintCaret(rc: RenderContext, theme: Theme, view: Rect, at: Point): void {
    if (!this.#caretOn || this.readOnly || !this.effectivelyEnabled) return;
    rc.fillRect(
      { x: view.x + at.x, y: view.y + at.y, width: 1, height: this.lineHeight },
      theme.palette.fieldText,
    );
  }

  protected override onPaintOverlay(rc: RenderContext, theme: Theme): void {
    this.paintScrollCorner(rc, theme);
  }
}

